// ============================================
// MANTENIMIENTOSEXP.JS — Vista de expedientes tipo Mantenimiento
// Calendar próximos 90 días + confirmación de próxima visita recurrente
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import ExpedienteDetalle from './components/ExpedienteDetalle';
import {
  API_BASE_EXP, PP_EXP, COL_EXP, ESTADOS_EXP,
  estiloBotonExp, estiloBotonSecExp, estiloTarjetaExp,
  estiloInputExp, formatFechaHora, formatMoneda,
} from './components/helpersExpedientes';

export default function MantenimientosExp({ esCelular }) {
  const [expedientes, setExpedientes] = useState([]);
  const [eventosProximos, setEventosProximos] = useState([]);
  const [tipoMantId, setTipoMantId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [modalRec, setModalRec] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const tipos = await fetch(`${API_BASE_EXP}/api/expedientes/tipos`).then(r => r.json());
      const tMant = (tipos.tipos || []).find(t => /mantenimiento/i.test(t.nombre));
      if (!tMant) { setExpedientes([]); setCargando(false); return; }
      setTipoMantId(tMant.id);

      const hoy = new Date();
      const desde = hoy.toISOString();
      const hasta = new Date(hoy.getTime() + 90 * 86400000).toISOString();

      const [lista, eventos] = await Promise.all([
        fetch(`${API_BASE_EXP}/api/expedientes?tipo_id=${tMant.id}`).then(r => r.json()),
        fetch(`${API_BASE_EXP}/api/expedientes/eventos/calendario?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}&tipo_id=${tMant.id}`).then(r => r.json()),
      ]);
      setExpedientes(lista.expedientes || []);
      setEventosProximos(eventos.eventos || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const recurrentesSinVisita = useMemo(() => {
    // Expedientes con contrato_recurrencia pero sin evento 'programado' en el horizonte
    return expedientes.filter(e => {
      if (!e.contrato_recurrencia) return false;
      if (e.estado === 'cancelado' || e.estado === 'pagado') return false;
      const tieneProximo = eventosProximos.some(ev => ev.expediente_id === e.id && ev.estado === 'programado');
      return !tieneProximo;
    });
  }, [expedientes, eventosProximos]);

  if (detalle) {
    return <ExpedienteDetalle id={detalle} onVolver={() => { setDetalle(null); cargar(); }} />;
  }

  return (
    <div style={{ fontFamily: PP_EXP, background: COL_EXP.fondo, minHeight: '100%', color: COL_EXP.gris }}>
      <div style={{
        padding: esCelular ? 14 : '18px 24px', borderBottom: `1px solid ${COL_EXP.borde}`, background: '#fff',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: COL_EXP.oscuro }}>🔨 Mantenimientos</div>
        <div style={{ fontSize: 12, color: COL_EXP.grisClaro, marginTop: 2 }}>
          {expedientes.length} contrato(s) · Próximos 90 días
        </div>
      </div>

      <div style={{ padding: esCelular ? 12 : 20, display: 'grid', gap: 14 }}>
        {cargando && <div style={{ textAlign: 'center', padding: 40, color: COL_EXP.grisClaro }}>Cargando…</div>}

        {recurrentesSinVisita.length > 0 && (
          <div style={{ ...estiloTarjetaExp, borderColor: '#f0d9b8', background: '#fff4e5' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#a86b1e', marginBottom: 10 }}>
              📅 Esperan confirmación de próxima visita ({recurrentesSinVisita.length})
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {recurrentesSinVisita.map(e => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setDetalle(e.id)}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COL_EXP.oscuro }}>{e.titulo}</div>
                    <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>
                      {e.folio} · {[e.grupo_nombre, e.sucursal_nombre].filter(Boolean).join(' · ')}
                      {e.contrato_recurrencia && e.contrato_recurrencia.frecuencia && (
                        <span> · 🔁 {e.contrato_recurrencia.frecuencia}</span>
                      )}
                    </div>
                  </div>
                  <button style={estiloBotonExp} onClick={() => setModalRec(e)}>Confirmar siguiente visita</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Próximas visitas programadas */}
        <div style={estiloTarjetaExp}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 10 }}>
            📆 Visitas programadas (próximos 90 días)
          </div>
          {eventosProximos.length === 0 && (
            <div style={{ fontSize: 12, color: COL_EXP.grisClaro, padding: '6px 0' }}>Sin visitas programadas.</div>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {eventosProximos.map(ev => (
              <div key={ev.id} onClick={() => setDetalle(ev.expediente_id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: COL_EXP.fondo, borderRadius: 8, cursor: 'pointer',
                borderLeft: `3px solid ${ev.tipo_color || COL_EXP.principal}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL_EXP.oscuro, minWidth: 120 }}>
                  {formatFechaHora(ev.fecha_inicio)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{ev.expediente_titulo}</div>
                  <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>
                    {ev.folio} · {ev.tipo_evento}
                    {ev.sucursal_nombre && ` · 📍 ${ev.sucursal_nombre}`}
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#eaf2fb', color: COL_EXP.oscuro, textTransform: 'uppercase' }}>
                  {ev.estado}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Contratos activos */}
        <div style={estiloTarjetaExp}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 10 }}>
            📋 Contratos de mantenimiento ({expedientes.length})
          </div>
          {expedientes.length === 0 && (
            <div style={{ fontSize: 12, color: COL_EXP.grisClaro, padding: '6px 0' }}>Sin contratos. Crea uno en "Todos los expedientes".</div>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {expedientes.map(e => {
              const estado = ESTADOS_EXP[e.estado] || { label: e.estado, color: COL_EXP.grisClaro, bg: COL_EXP.fondo };
              return (
                <div key={e.id} onClick={() => setDetalle(e.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  background: COL_EXP.fondo, borderRadius: 8, cursor: 'pointer',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{e.titulo}</div>
                    <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>
                      {e.folio} · {[e.grupo_nombre, e.sucursal_nombre].filter(Boolean).join(' · ') || 'Sin cliente'}
                      {e.monto_estimado && ` · ${formatMoneda(e.monto_estimado)}`}
                    </div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: estado.bg, color: estado.color, textTransform: 'uppercase' }}>
                    {estado.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {modalRec && (
        <ConfirmarRecurrenciaModal
          expediente={modalRec}
          onCerrar={() => setModalRec(null)}
          onCreado={() => { setModalRec(null); cargar(); }}
        />
      )}
    </div>
  );
}

function ConfirmarRecurrenciaModal({ expediente, onCerrar, onCreado }) {
  const [fecha, setFecha] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('beep_token');
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_EXP}/api/usuarios`, { headers: h }).then(r => r.json()).catch(() => ({ usuarios: [] }))
      .then(u => setTecnicos(u.usuarios || []));
    // sugerir fecha según frecuencia
    const c = expediente.contrato_recurrencia;
    if (c) {
      const periodo = {
        semanal: 7, quincenal: 15, mensual: 30, bimestral: 60,
        trimestral: 90, semestral: 180, anual: 365,
      }[c.frecuencia] || 30;
      const sugerida = new Date(Date.now() + periodo * (c.cada || 1) * 86400000);
      sugerida.setHours(10, 0, 0);
      setFecha(sugerida.toISOString().slice(0, 16));
    }
  }, [expediente]);

  const guardar = async () => {
    if (!fecha) return;
    setGuardando(true);
    try {
      await fetch(`${API_BASE_EXP}/api/expedientes/${expediente.id}/proxima-recurrencia`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha_inicio: fecha, tecnico_id: tecnicoId || null }),
      });
      onCreado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.5)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460,
        fontFamily: PP_EXP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 8 }}>
          📅 Confirmar próxima visita
        </div>
        <div style={{ fontSize: 12, color: COL_EXP.grisClaro, marginBottom: 14 }}>
          {expediente.titulo} · {expediente.folio}
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: COL_EXP.grisClaro }}>Fecha y hora</label>
            <input type="datetime-local" style={estiloInputExp} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: COL_EXP.grisClaro }}>Técnico</label>
            <select style={estiloInputExp} value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
              <option value="">— Sin asignar —</option>
              {tecnicos.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecExp} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonExp} onClick={guardar} disabled={!fecha || guardando}>
            {guardando ? 'Creando…' : 'Programar'}
          </button>
        </div>
      </div>
    </div>
  );
}
